// ============================================================
// useAutoSave.ts
// Custom hook that automatically saves case data to the backend
// whenever the current case changes in the Zustand store.
// Uses a debounce of 1000ms so we don't fire on every keystroke.
//
// Also exports flushAutoSave() — call it before navigating away to
// guarantee any pending edit is persisted instead of lost.
// ============================================================
import { useEffect } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { updateCaseApi, fetchCaseById } from '../api/casevoApi'
import { serverChangedTouchedColumns, normaliseServerDates, MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'
import { Case } from '../types/case.types'
import { getCaseBaseUpdatedAt, setCaseBaseUpdatedAt } from './saveBaseVersion'
import { flushCollabSnapshots } from '../components/shared/RichTextEditor'
import { caseScope, refusedField, useFieldRefusalStore } from './fieldRefusals'
import { holdInvalidContact } from '../utils/contactFormat'
import { getHeldContact, setHeldContact, type HeldContactKind } from './heldContact'
import { emitCaseChange } from '../utils/caseChangeEvents'

// The case copy's contact columns (flattenCase) — for holdInvalidContact.
const CASE_CONTACT_KEYS = { email: 'clientEmailAddress', phone: 'clientMainContactNumber', hasEmail: 'clientHasEmailAddress' }
// (Batch 2) Client-details columns the Settlement page + statement panel show
// (DOB → child / adult, email, address): a save touching one announces
// 'clientDetails' (utils/caseChangeEvents) so they re-read.
const SETTLEMENT_CLIENT_COLUMNS = new Set([
  'clientDateOfBirth', 'clientEmailAddress', 'clientHasEmailAddress', 'clientForename', 'clientSurname', 'clientTitle',
  'clientAddressLine1', 'clientAddressLine2', 'clientAddressTown', 'clientAddressCounty', 'clientAddressPostcode',
])

// Converts the nested Case structure back to the flat format the
// backend expects — mirrors the mapping in useCasevoStore.
//
// Stage 2b cutover: the case-page Client Details editor now writes to
// the case-row CLIENT SNAPSHOT (currentCase.clientDetails) rather than
// the live Client. flattenCase therefore emits the full set of snapshot
// columns — 15 person + 14 GP/Hospital — that mirror Reuben's
// syncClientSnapshot field list in backend/src/controllers/caseController.ts
// (the only proven-correct names; Prisma 500s on unknown columns, so
// the names below MUST stay 1:1 with the backend schema). The live
// Client is now edited from ClientSearch only and persisted via
// PATCH /api/clients/:id by useClientAutoSave (a different singleton).
//
// The 4 case-specific client fields (timeOffWork, daysOffWork,
// descriptionOfInjuries, medicalAttention) describe the incident, not
// the person, and have always been on this PUT.
export const flattenCase = (c: Case): Record<string, unknown> => ({
  caseName: c.caseName ?? null,
  handover: c.caseControl.handover,
  openDate: c.caseControl.openDate,
  closeDate: c.caseControl.closeDate,
  setupFinalised: c.caseControl.setupFinalised,
  // Client Source
  clientSource: c.clientSource.name,
  clientSourceContactNumber: c.clientSource.contactNumber,
  clientSourceEmailAddress: c.clientSource.emailAddress,
  clientSourceAddressLine1: c.clientSource.addressLine1,
  clientSourceAddressLine2: c.clientSource.addressLine2,
  clientSourceAddressTown: c.clientSource.addressTown,
  clientSourceAddressCounty: c.clientSource.addressCounty,
  clientSourceAddressPostcode: c.clientSource.addressPostcode,
  clientSourceNotApplicable: c.clientSource.notApplicable,
  // ── Client snapshot — PERSON FIELDS (15) ─────────────────────
  // Column names verbatim from syncClientSnapshot (caseController.ts
  // L358-372). Any rename here MUST match the backend schema, or
  // Prisma will reject the whole PUT with "unknown field" → 500.
  clientTitle: c.clientDetails.title,
  clientForename: c.clientDetails.forename,
  clientSurname: c.clientDetails.surname,
  clientAddressLine1: c.clientDetails.addressLine1,
  clientAddressLine2: c.clientDetails.addressLine2,
  clientAddressTown: c.clientDetails.addressTown,
  clientAddressCounty: c.clientDetails.addressCounty,
  clientAddressPostcode: c.clientDetails.addressPostcode,
  clientDateOfBirth: c.clientDetails.dateOfBirth,
  clientHasNiNumber: c.clientDetails.hasNiNumber,
  clientNiNumber: c.clientDetails.niNumber,
  clientMainContactNumber: c.clientDetails.mainContactNumber,
  clientEmailAddress: c.clientDetails.emailAddress,
  clientHasEmailAddress: c.clientDetails.hasEmailAddress ?? null,
  clientEmploymentStatus: c.clientDetails.employmentStatus,
  clientOccupation: c.clientDetails.occupation,
  // ── Client snapshot — GP block (7) ───────────────────────────
  clientGpName: c.clientDetails.gpName,
  clientGpAddressLine1: c.clientDetails.gpAddressLine1,
  clientGpAddressLine2: c.clientDetails.gpAddressLine2,
  clientGpAddressTown: c.clientDetails.gpAddressTown,
  clientGpAddressCounty: c.clientDetails.gpAddressCounty,
  clientGpAddressPostcode: c.clientDetails.gpAddressPostcode,
  clientGpPhone: c.clientDetails.gpPhone,
  // ── Client snapshot — Hospital block (7) ─────────────────────
  clientHospitalName: c.clientDetails.hospitalName,
  clientHospitalAddressLine1: c.clientDetails.hospitalAddressLine1,
  clientHospitalAddressLine2: c.clientDetails.hospitalAddressLine2,
  clientHospitalAddressTown: c.clientDetails.hospitalAddressTown,
  clientHospitalAddressCounty: c.clientDetails.hospitalAddressCounty,
  clientHospitalAddressPostcode: c.clientDetails.hospitalAddressPostcode,
  clientHospitalPhone: c.clientDetails.hospitalPhone,
  // ── Case-specific client fields (incident attributes) ───────
  clientTimeOffWork: c.clientDetails.timeOffWork,
  clientDaysOffWork: c.clientDetails.daysOffWork,
  clientDescriptionOfInjuries: c.clientDetails.descriptionOfInjuries,
  clientMedicalAttention: c.clientDetails.medicalAttention,
  askCueReference: c.accidentDetails.askCueReference,
  accidentDate: c.accidentDetails.accidentDate,
  accidentTime: c.accidentDetails.accidentTime,
  vehiclePosition: c.accidentDetails.vehiclePosition,
  driverName: c.accidentDetails.driverName,
  accidentDescription: c.accidentDetails.accidentDescription,
  accidentDescriptionOther: c.accidentDetails.accidentDescriptionOther,
  accidentLocation: c.accidentDetails.accidentLocation,
  accidentLocationMode: c.accidentDetails.accidentLocationMode,
  accidentLocationFreeText: c.accidentDetails.accidentLocationFreeText,
  accidentLocationAddressLine1: c.accidentDetails.accidentLocationAddressLine1,
  accidentLocationAddressLine2: c.accidentDetails.accidentLocationAddressLine2,
  accidentLocationAddressTown: c.accidentDetails.accidentLocationAddressTown,
  accidentLocationAddressCounty: c.accidentDetails.accidentLocationAddressCounty,
  accidentLocationAddressPostcode: c.accidentDetails.accidentLocationAddressPostcode,
  wearingSeatbelt: c.accidentDetails.wearingSeatbelt,
  claimantReg: c.accidentDetails.claimantReg,
  claimantVehicleResponsible: c.accidentDetails.claimantVehicleResponsible,
  numberOfPeopleInVehicle: c.accidentDetails.numberOfPeopleInVehicle,
  policeInvolvement: c.accidentDetails.policeInvolvement,
  injuryNeck: c.injuryDetails.neck,
  injuryShoulder: c.injuryDetails.shoulder,
  injuryBack: c.injuryDetails.back,
  injuryRibsChestTorso: c.injuryDetails.ribsChestTorso,
  injuryFaceCheckboneJawNose: c.injuryDetails.faceCheckboneJawNose,
  injuryForearmWristHandFingers: c.injuryDetails.forearmWristHandFingers,
  injuryDamageToTeeth: c.injuryDetails.damageToTeeth,
  injuryHeadSenses: c.injuryDetails.headSenses,
  injuryHipsPelvisGenitals: c.injuryDetails.hipsPelvisGenitals,
  injuryLegKneeAnkleFootToe: c.injuryDetails.legKneeAnkleFootToe,
  injuryPsychological: c.injuryDetails.psychological,
  // B3 (B2c item 4)
  injuryHearingEars: c.injuryDetails.hearingEars,
  injuryLungsBreathing: c.injuryDetails.lungsBreathing,
  injurySkin: c.injuryDetails.skin,
  injuryInternalOrgans: c.injuryDetails.internalOrgans,
  injuryFatal: c.injuryDetails.fatal,
  injuryOther: c.injuryDetails.other,
  injuryOtherDetails: c.injuryDetails.otherDetails,
  injuryExceptionallySevere: c.injuryDetails.exceptionallySevere,
  injuryExceptionallySevereDetails: c.injuryDetails.exceptionallySevereDetails,
  defendantReg: c.defendant.reg,
  defendantName: c.defendant.name,
  defendantAddressLine1: c.defendant.address?.line1 || '',
  defendantAddressLine2: c.defendant.address?.line2 || '',
  defendantAddressTown: c.defendant.address?.town || '',
  defendantAddressCounty: c.defendant.address?.county || '',
  defendantAddressPostcode: c.defendant.address?.postcode || '',
  defendantMainContactNumber: c.defendant.mainContactNumber,
  tpiInsurer: c.tpi.insurer,
  tpiReferenceNumber: c.tpi.referenceNumber,
  tpiAddressLine1: c.tpi.address?.line1 || '',
  tpiAddressLine2: c.tpi.address?.line2 || '',
  tpiAddressTown: c.tpi.address?.town || '',
  tpiAddressCounty: c.tpi.address?.county || '',
  tpiAddressPostcode: c.tpi.address?.postcode || '',
  linkedCases: c.linkedCases,
  medicalAgencyName: c.medicalAgencyName,
  medicalAgencyAddressLine1: c.medicalAgencyAddressLine1,
  medicalAgencyAddressLine2: c.medicalAgencyAddressLine2,
  medicalAgencyAddressTown: c.medicalAgencyAddressTown,
  medicalAgencyAddressCounty: c.medicalAgencyAddressCounty,
  medicalAgencyAddressPostcode: c.medicalAgencyAddressPostcode,
  medicalAgencyPhone: c.medicalAgencyPhone,
  medicalAgencyEmail: c.medicalAgencyEmail,
  medicalAgencyNotes: c.medicalAgencyNotes,
  medicalExpertName: c.medicalExpertName,
  medicalExpertAddressLine1: c.medicalExpertAddressLine1,
  medicalExpertAddressLine2: c.medicalExpertAddressLine2,
  medicalExpertAddressTown: c.medicalExpertAddressTown,
  medicalExpertAddressCounty: c.medicalExpertAddressCounty,
  medicalExpertAddressPostcode: c.medicalExpertAddressPostcode,
  medicalExpertPhone: c.medicalExpertPhone,
  medicalExpertEmail: c.medicalExpertEmail,
  medicalExpertNotes: c.medicalExpertNotes,
  tpsName: c.tpsName,
  tpsAddressLine1: c.tpsAddressLine1,
  tpsAddressLine2: c.tpsAddressLine2,
  tpsAddressTown: c.tpsAddressTown,
  tpsAddressCounty: c.tpsAddressCounty,
  tpsAddressPostcode: c.tpsAddressPostcode,
  tpsPhone: c.tpsPhone,
  tpsEmail: c.tpsEmail,
  tpsNotes: c.tpsNotes,
  // Funding / OIC fields. Backend columns are plain strings; the
  // FundingSection clears deductionCustomValue in the same
  // updateCase call when deductionMode switches away from 'other',
  // so the backend persists '' rather than stale custom data.
  deductionMode: c.deductionMode,
  deductionCustomValue: c.deductionCustomValue,
  ateProvider: c.ateProvider,
  // Claim group (B2). Only CHANGED columns are sent (diffFlat), so an old
  // case's unset claim type is never PUT as null. The server checks each
  // (lib/caseClaim) and refuses ONE field with a 400 naming it.
  claimType: c.claim?.claimType,
  claimRoute: c.claim?.claimRoute,
  track: c.claim?.track,
  complexityBand: c.claim?.complexityBand,
  frcRegime: c.claim?.frcRegime,
  dateOfKnowledge: c.claim?.dateOfKnowledge,
  dateOfDeath: c.claim?.dateOfDeath,
  limitationOverrideDate: c.claim?.limitationOverrideDate,
  limitationOverrideReason: c.claim?.limitationOverrideReason,
})

// The case-store patch that puts ONE flat column back to `value` — the date
// columns the server can refuse live in nested groups; a top-level scalar
// column maps 1:1. null = no known place (the caller then just stops
// re-sending it).
const NESTED_COLUMNS: Record<string, [keyof Case, string]> = {
  accidentDate: ['accidentDetails', 'accidentDate'],
  clientDateOfBirth: ['clientDetails', 'dateOfBirth'],
  openDate: ['caseControl', 'openDate'],
  closeDate: ['caseControl', 'closeDate'],
  handover: ['caseControl', 'handover'],
  claimType: ['claim', 'claimType'],
  claimRoute: ['claim', 'claimRoute'],
  track: ['claim', 'track'],
  complexityBand: ['claim', 'complexityBand'],
  frcRegime: ['claim', 'frcRegime'],
  dateOfKnowledge: ['claim', 'dateOfKnowledge'],
  dateOfDeath: ['claim', 'dateOfDeath'],
  limitationOverrideDate: ['claim', 'limitationOverrideDate'],
  limitationOverrideReason: ['claim', 'limitationOverrideReason'],
}
export const casePatchFor = (c: Case, column: string, value: unknown): Partial<Case> | null => {
  const nested = NESTED_COLUMNS[column]
  if (nested) {
    const [group, key] = nested
    const g = c[group] as unknown as Record<string, unknown> | undefined
    if (!g || typeof g !== 'object') return null
    return { [group]: { ...g, [key]: value } } as Partial<Case>
  }
  const top = (c as unknown as Record<string, unknown>)[column]
  if (Object.prototype.hasOwnProperty.call(c, column) && (top === null || typeof top !== 'object')) return { [column]: value } as Partial<Case>
  return null
}

// Case columns that are Yjs-collaborative rich-text fields (Stage 1 rollout).
// Their string column is a PASSIVE HTML MIRROR — Yjs owns the content merge, so
// a 409 caused by one of these columns must NEVER surface the conflict toast
// (two co-editors both snapshot-save the same column, which reads as an
// "overlap" but is not a real conflict; the authoritative content is the live
// Y.Doc, and the next snapshot already carries the merged HTML). The 409
// handler below excludes these from the overlap decision AND from the toast.
const CASE_COLLAB_COLUMNS = new Set<string>([
  'medicalAgencyNotes',
  'medicalExpertNotes',
  'tpsNotes',
  // Stage 2 injury-prose fields (flat column names as flattenCase emits them).
  'clientDescriptionOfInjuries',
  'injuryExceptionallySevereDetails',
  // Stage 4 accident free-text (flattenCase emits `accidentLocationFreeText`).
  'accidentLocationFreeText',
])

// Module-scope state. useAutoSave() is mounted exactly once (in App.tsx),
// so a singleton is safe and lets flushAutoSave() be a plain function the
// nav handlers can import without going through React refs.
//
// pendingTimerId  — the active 1s debounce timer (null when nothing's pending)
// pendingPayload  — what would be saved when the timer fires (latest snapshot)
// inFlightPromise — the currently-running save, if any (null when idle)
// lastSavedRaw    — the last successfully-persisted serialised state
// lastError       — the most recent save failure, mirrored into the store
//                   so SaveIndicator can render it. Cleared on next success.
// retryTimerId    — the pending one-shot 5s retry timer (null when none).
//                   Cancelled when the user types something new (so the
//                   normal debounce handles the fresher data) and when a
//                   save succeeds (no need to retry anything).
type PendingPayload = {
  id: string
  flat: Record<string, unknown>
  serialised: string
  // ★ Set only for an ORPHANED edit — a pending save for case A captured at
  // the moment the user switched to case B. It carries case A's own saved
  // baseline so the diff is measured against A (not against B's freshly
  // seeded lastSavedFlat) and its success never overwrites B's baseline.
  baseFlat?: Record<string, unknown> | null
}
let pendingTimerId: ReturnType<typeof setTimeout> | null = null
let pendingPayload: PendingPayload | null = null
let inFlightPromise: Promise<void> | null = null
let lastSavedRaw: string | null = null
let lastError: Error | null = null
let retryTimerId: ReturnType<typeof setTimeout> | null = null
// PARTIAL-SAVE DIFF BASELINE.
// lastSavedFlat  — the flattenCase() result at the last SUCCESSFUL save (or
//                  the freshly-loaded case, seeded on load). runSave diffs the
//                  current flat against this and sends ONLY the changed keys,
//                  so two users editing DIFFERENT fields no longer clobber each
//                  other (each PUT touches only its own changed columns).
// lastSeededCaseId — the case id lastSavedFlat/lastSavedRaw were seeded for.
//                  When a DIFFERENT case loads we re-seed the baseline (loading
//                  is not an edit) so the first real edit diffs against the
//                  loaded values, not a previous case's snapshot.
let lastSavedFlat: Record<string, unknown> | null = null
let lastSeededCaseId: string | null = null

// Return only the keys of `current` whose value CHANGED vs `base`. This is a
// value-INEQUALITY test (v !== base[k]), NOT a truthiness check — so a field
// cleared to '' or null is INCLUDED (it differs from its old value) rather than
// dropped. With no baseline yet (shouldn't happen after load-seeding) it falls
// back to the full record so nothing is silently lost.
const diffFlat = (
  current: Record<string, unknown>,
  base: Record<string, unknown> | null,
): Record<string, unknown> => {
  if (base === null) return { ...current }
  const changed: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(current)) {
    if (v !== base[k]) changed[k] = v
  }
  return changed
}

// (M17) Which contact values the next save would HOLD BACK as invalid.
export const heldContactOf = (flat: Record<string, unknown>, base: Record<string, unknown> | null): HeldContactKind[] => {
  const keys = holdInvalidContact(diffFlat(flat, base), flat, CASE_CONTACT_KEYS)
  return keys.map((k) => (k === CASE_CONTACT_KEYS.email ? 'email' : 'phone'))
}

// Mirror lastError into the store so React subscribers (SaveIndicator)
// re-render. We're outside React here, so use the imperative
// getState() escape hatch.
const publishError = (err: Error | null): void => {
  lastError = err
  useCasevoStore.getState().setSaveError(err)
}

const RETRY_DELAY_MS = 5000

// Fires the actual PATCH request and tracks in-flight + error state.
//
// On success: lastSavedRaw advances, the matching pending payload
// clears, any error is cleared, and any pending retry is cancelled.
//
// On failure: the error is stored and published to the Zustand store so
// SaveIndicator can render it. If `suppressRetry` is false (the default
// for normal debounce-triggered saves), a single 5-second retry is
// scheduled — but only one. The retry itself calls runSave with
// suppressRetry=true so a second failure doesn't keep retrying. flush
// also passes suppressRetry=true (the user has navigated away by then;
// retrying in the background is wasted work).
const runSave = (
  payload: PendingPayload,
  suppressRetry: boolean = false,
  // Column-aware 409 recovery attempt counter (0 = first try). A silent
  // re-apply increments it; capped by MAX_409_REAPPLY_ATTEMPTS.
  attempt: number = 0,
): Promise<void> => {
  // Placeholder so the IIFE's `finally` can refer to `promise` — by the
  // time `finally` actually runs, the reassignment below has happened.
  let promise: Promise<void> = Promise.resolve()
  promise = (async () => {
    // Captured before the try so the 409 branch (catch) sees the same base.
    const baseFlat = payload.baseFlat !== undefined ? payload.baseFlat : lastSavedFlat
    try {
      // Send ONLY the columns that changed since the last save (id is always
      // carried by the URL, so it's never part of the diff body). If nothing
      // changed at the column level — e.g. an edit that netted back to the
      // saved value, or only the local updatedAt stamp moved (updatedAt isn't
      // in flattenCase) — skip the network call entirely but still settle as a
      // success below so pending/error state clears.
      const changed = diffFlat(payload.flat, baseFlat)
      // A half-typed email / phone isn't sent yet (utils/contactFormat).
      const held = holdInvalidContact(changed, payload.flat, CASE_CONTACT_KEYS)
      // A field being sent again (edited since the server refused it) drops
      // its old refusal note; a fresh refusal sets it again below.
      for (const k of Object.keys(changed)) useFieldRefusalStore.getState().clearRefusal(caseScope(payload.id), k)
      if (Object.keys(changed).length > 0) {
        // Optimistic-concurrency: send the SERVER updatedAt we loaded so the
        // backend can 409 if someone else saved since. Opt-in — if we never
        // captured a base (shouldn't happen post-load), omit it and the
        // backend behaves as before.
        const base = getCaseBaseUpdatedAt(payload.id)
        const body = base !== null ? { ...changed, baseUpdatedAt: base } : changed
        const result = await updateCaseApi(payload.id, body)
        // Advance the concurrency baseline to the server's new updatedAt so
        // the NEXT save's base is current.
        setCaseBaseUpdatedAt(payload.id, result.updatedAt)
        if (Object.keys(changed).some((k) => SETTLEMENT_CLIENT_COLUMNS.has(k))) emitCaseChange(payload.id, 'clientDetails')
        // (Stage 3) The firm-wide CASE LIST patch is now SERVER-driven: the
        // updateCase controller emits entity-changed op:'update', so we no
        // longer broadcast a client-side list-update here. The case list
        // patches + advances its base from that event (CaseListScreen).
      }
      // Advance the diff baseline to the full just-saved snapshot so the NEXT
      // diff is measured against the state now on the server — but ONLY for
      // the case that is currently seeded. An orphaned save for a case the
      // user has since left must not overwrite the open case's baseline.
      if (payload.baseFlat === undefined && payload.id === lastSeededCaseId) {
        lastSavedRaw = payload.serialised
        // A held-back value stays at its saved value in the baseline, so it's
        // sent once it's valid.
        lastSavedFlat = held.length
          ? { ...payload.flat, ...Object.fromEntries(held.map((k) => [k, (baseFlat ?? {})[k]])) }
          : payload.flat
      }
      // Only clear pendingPayload if it's still the same one we just saved;
      // a newer keystroke during the request may have replaced it already.
      if (pendingPayload && pendingPayload.serialised === payload.serialised) {
        pendingPayload = null
      }
      // Success — clear any prior error and cancel any pending retry.
      if (lastError !== null) publishError(null)
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId)
        retryTimerId = null
      }
    } catch (err) {
      // 409 = optimistic-concurrency conflict: someone else saved this case
      // first. NON-DESTRUCTIVE — we do NOT overwrite their change and we do
      // NOT wipe the user's in-progress edit (it stays in currentCase / on
      // screen). We: (1) advance the baseline to the server's current
      // updatedAt from the 409 body (equivalent to a re-seed, without
      // clobbering the working copy), (2) cancel any pending auto-retry and
      // DROP the stale pending payload so the debounce/flush can't silently
      // resubmit it, and (3) warn the user via the global conflict toast. A
      // subsequent keystroke re-stages a fresh save against the new baseline.
      const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
      if (resp?.status === 409) {
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }
        // COLUMN-AWARE recovery: the guard is timestamp-based, so this may be a
        // SPURIOUS 409 (someone else edited DIFFERENT fields). Re-fetch and, if
        // the server didn't change any of the columns THIS save touched (vs our
        // base = lastSavedFlat), silently re-apply on the fresh base — no toast.
        // Only a genuine same-column clash (or exhausted retries) surfaces.
        const touched = Object.keys(diffFlat(payload.flat, baseFlat))
        // Collab (Yjs) rich-text columns are a passive HTML mirror — Yjs owns
        // their merge, so they must never drive a conflict. Both the overlap
        // decision and the toast below consider ONLY the NON-collab columns; a
        // 409 caused solely by a collab column is always resolved silently.
        const nonCollabTouched = touched.filter((c) => !CASE_COLLAB_COLUMNS.has(c))
        if (attempt < MAX_409_REAPPLY_ATTEMPTS && touched.length > 0) {
          try {
            const server = await fetchCaseById(payload.id)
            const overlap = serverChangedTouchedColumns(
              nonCollabTouched, baseFlat ?? {}, normaliseServerDates(server as unknown as Record<string, unknown>), payload.flat,
            )
            if (!overlap) {
              // No real (non-collab) overlap: advance to the fresh server
              // updatedAt (includes the other user's change) and re-send ONLY our
              // columns. suppressRetry so a failure doesn't also schedule the 5s
              // timer. Covers the pure-collab case (nonCollabTouched empty →
              // overlap false → always silent).
              setCaseBaseUpdatedAt(payload.id, server.updatedAt)
              await runSave(payload, true, attempt + 1)
              return
            }
          } catch { /* re-fetch failed — fall through to the conflict path */ }
        }
        // Fall-through: a genuine NON-collab same-column conflict, retry cap hit,
        // or re-fetch failed. Advance base + drop the stale payload either way.
        if (resp.data?.currentUpdatedAt) setCaseBaseUpdatedAt(payload.id, resp.data.currentUpdatedAt)
        if (pendingPayload && pendingPayload.serialised === payload.serialised) {
          pendingPayload = null
        }
        if (lastError !== null) publishError(null)
        // Only a genuine NON-collab conflict warns. If the columns in play were
        // ALL collab (Yjs) fields, resolve silently — Yjs is the content source
        // of truth and the next snapshot carries the merged HTML.
        if (nonCollabTouched.length === 0) return
        useCasevoStore.getState().setSaveConflict(
          "This case was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
        )
        return
      }
      // ★ 400 naming ONE field (e.g. an accident date the server refuses):
      // that field goes back to its SAVED value, its message shows under it
      // (hooks/fieldRefusals), and the OTHER changes in the batch still save.
      // Without this the refused value stayed in every later diff and every
      // later autosave on the case 400'd too.
      const refused = refusedField(err)
      if (refused && refused.field in payload.flat) {
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }
        useFieldRefusalStore.getState().setRefusal(caseScope(payload.id), refused.field, refused.message)
        if (lastError !== null) publishError(null)
        const savedValue = (baseFlat ?? {})[refused.field]
        const open = useCasevoStore.getState().currentCase
        const revert = open && open.id === payload.id && payload.baseFlat === undefined ? casePatchFor(open, refused.field, savedValue) : null
        if (revert) {
          // Show the saved value again; the autosave effect then re-stages
          // whatever else changed (the refused field now equals its baseline,
          // so it's no longer in the diff).
          if (pendingPayload && pendingPayload.serialised === payload.serialised) pendingPayload = null
          useCasevoStore.getState().updateCase(revert)
          return
        }
        // Can't put it back on screen (an orphaned save, or a field with no
        // known place in the case) → send everything ELSE now, with the
        // refused field at its saved value.
        const rest: PendingPayload = { ...payload, flat: { ...payload.flat, [refused.field]: savedValue } }
        if (Object.keys(diffFlat(rest.flat, baseFlat)).length > 0) await runSave(rest, true, attempt)
        return
      }
      const error = err instanceof Error ? err : new Error('Auto-save failed')
      publishError(error)
      // eslint-disable-next-line no-console
      console.error('[AutoSave] Failed to save case:', err)
      // Schedule one retry — unless we're already in a retry, or the
      // caller (flush) explicitly suppressed it.
      if (!suppressRetry) {
        if (retryTimerId !== null) clearTimeout(retryTimerId)
        retryTimerId = setTimeout(() => {
          retryTimerId = null
          // Use the LATEST pendingPayload — if the user has typed since,
          // we save the fresher data instead of the original. If
          // pendingPayload is null, an in-between flush already saved
          // and we have nothing to retry.
          if (pendingPayload) runSave(pendingPayload, true)
        }, RETRY_DELAY_MS)
      }
    } finally {
      // Only clear the in-flight slot if WE were the in-flight promise;
      // a concurrent flush may have started another save by now.
      if (inFlightPromise === promise) inFlightPromise = null
    }
  })()
  inFlightPromise = promise
  return promise
}

// Public: manual retry of the last failed save, triggered by the
// SaveIndicator's "Try again" button. No chaining — one click is one
// attempt; if it fails again, the user can click again.
//
// Behaviour:
//   - No-op when there's no error to recover from.
//   - Cancels the pending 5s auto-retry (we're firing now, manually).
//   - Awaits any in-flight save so we don't run two saves in parallel
//     against the same case. If that in-flight save ends up succeeding,
//     the error + payload are already cleared and there's nothing left
//     for us to do.
//   - Calls runSave with suppressRetry=true so a manual retry that
//     fails doesn't kick off another 5s auto-retry timer — the user
//     stays in control of when to try again.
export const retryAutoSave = async (): Promise<void> => {
  if (lastError === null) return
  if (retryTimerId !== null) {
    clearTimeout(retryTimerId)
    retryTimerId = null
  }
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  // After the in-flight settles, re-check: a successful auto-retry
  // would already have cleared both. Bail out either way — the UI
  // is correct.
  if (lastError === null || pendingPayload === null) return
  await runSave(pendingPayload, true)
}

// Public: forces any pending edit to persist now. Call before navigating
// away from a case detail screen so the user doesn't lose a sub-second
// debounce window. Safe to call when nothing's pending — resolves
// immediately. Safe to call mid-flight — won't fire a duplicate; awaits
// the in-flight save (and, if a newer keystroke landed during the
// request, fires a follow-up save for that newer state).
export const flushAutoSave = async (): Promise<void> => {
  // ★ (case-page audit) Push any mounted live-doc editor's debounced HTML
  // snapshot (case notes etc., ~1.5s debounce) into the store FIRST. Exit
  // paths that call only flushAutoSave (sidebar / header nav, popstate,
  // opening a linked case) otherwise left the last ~1.5s of note typing
  // un-mirrored. The snapshot's updateCase is synchronous, but the autosave
  // EFFECT that stages it runs later — so stage it here directly from the
  // store (same shape the effect builds) when it's the seeded case — but ONLY
  // when a snapshot actually changed the case (so a flush never re-stages an
  // edit a genuine 409 deliberately dropped).
  const beforeFlush = useCasevoStore.getState().currentCase
  flushCollabSnapshots()
  const live = useCasevoStore.getState().currentCase
  if (live && live !== beforeFlush && live.id === lastSeededCaseId && (!pendingPayload || pendingPayload.id === live.id)) {
    const s = JSON.stringify(live)
    if (s !== lastSavedRaw && (!pendingPayload || pendingPayload.serialised !== s)) {
      pendingPayload = { id: live.id, flat: flattenCase(live), serialised: s }
    }
  }
  // Cancel the debounce — we're firing now (or there's nothing to fire).
  if (pendingTimerId !== null) {
    clearTimeout(pendingTimerId)
    pendingTimerId = null
  }
  // Wait for any save already on the wire to settle, so we don't race.
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  // If a newer edit is still pending after the in-flight save resolved,
  // fire it now and wait for it too. suppressRetry=true so a failed
  // flush doesn't keep retrying after the user has navigated away.
  if (pendingPayload) {
    try { await runSave(pendingPayload, true) } catch { /* logged in runSave */ }
  }
}

// ── Stage 1a: apply a REMOTE edit without triggering a save-loop ──────
// When a co-editor blurs a field, we receive their final value and apply it
// to currentCase via updateCase. But the autosave effect subscribes to the
// WHOLE currentCase: a naive updateCase would make `serialised !== lastSavedRaw`
// and diffFlat find changed columns → we'd re-save THEIR value → the server
// re-broadcasts → loop. And even a one-shot "skip" flag wouldn't be enough,
// because the baselines would stay stale and the user's NEXT local edit would
// diff against the old baseline and re-send the remote value too.
//
// The fix lives HERE because lastSavedRaw + lastSavedFlat are module-scope in
// this file and not exported. applyRemoteCaseEdit (a) applies the edit, then
// (b) IMMEDIATELY advances BOTH baselines to the post-apply state. The effect
// then sees serialised === lastSavedRaw and returns before staging anything;
// even if it didn't, diffFlat(current, lastSavedFlat) would find zero changed
// columns. Either way: ZERO saves staged — no loop.
//
// It also advances the optimistic-concurrency baseline to the SERVER updatedAt
// the remote save produced (carried in the message), so this client's next
// save sends a current baseUpdatedAt and won't spuriously 409 against the
// remote write. NEVER uses currentCase.updatedAt (the store stamps that
// locally on updateCase — it is not a server value).
//
// No-ops when the applied-to case isn't the one currently open (a late
// message for a case the user already navigated away from), so a stale edit
// can't resurrect a closed case's baseline.
export const applyRemoteCaseEdit = (
  updates: Partial<Case>,
  serverUpdatedAt: string,
): void => {
  const store = useCasevoStore.getState()
  const open = store.currentCase
  if (!open) return
  // ★ (case-page audit) Does THIS user have an unsaved local edit to this
  // case (staged in the 1s debounce, or not yet staged)? If so, advancing the
  // whole baseline to the post-apply state (the old behaviour) silently
  // ABSORBED that edit into the "saved" baseline — the effect cleanup had
  // cancelled its timer, the effect then saw serialised === lastSavedRaw and
  // never re-staged it, and a later flush of the stale pendingPayload sent the
  // co-editor's column back at its OLD value (reverting their change) while
  // never sending ours. Now: with a local edit outstanding we advance the diff
  // baseline ONLY for the columns the remote edit changed, and leave
  // lastSavedRaw alone so the effect re-stages the local edit against that
  // base (sending only our own columns).
  const beforeFlat = flattenCase(open)
  const hadLocalEdit =
    (pendingPayload !== null && pendingPayload.id === open.id) ||
    (lastSeededCaseId === open.id && lastSavedRaw !== null && JSON.stringify(open) !== lastSavedRaw)
  store.updateCase(updates)
  // Re-read post-apply: updateCase replaced currentCase with the merged object
  // (+ a locally-stamped updatedAt we deliberately ignore for the base).
  const applied = useCasevoStore.getState().currentCase
  if (!applied) return
  const appliedFlat = flattenCase(applied)
  if (hadLocalEdit && lastSavedFlat !== null && lastSeededCaseId === applied.id) {
    const base: Record<string, unknown> = { ...lastSavedFlat }
    for (const [k, v] of Object.entries(appliedFlat)) {
      if (v !== beforeFlat[k]) base[k] = v
    }
    lastSavedFlat = base
    // Replace the now-stale staged payload (it carried the co-editor's column
    // at its OLD value) with the merged state, so a flush in the gap before
    // the autosave effect re-runs (it re-stages + re-arms the timer) still
    // sends our edit — and only our columns.
    if (pendingPayload && pendingPayload.id === applied.id) {
      pendingPayload = { id: applied.id, flat: appliedFlat, serialised: JSON.stringify(applied) }
    }
  } else {
    lastSavedRaw = JSON.stringify(applied)
    lastSavedFlat = appliedFlat
  }
  // Keep the diff-baseline seed id aligned so the effect's case-change branch
  // doesn't re-seed (it's the same case) and treat the apply as a fresh load.
  lastSeededCaseId = applied.id
  setCaseBaseUpdatedAt(applied.id, serverUpdatedAt)
}

// ── (D12 live updates) a whole-case re-read after someone else's change ──
// hasUnsavedCaseEdit: does THIS user have a case edit not yet saved (typed,
// staged, in flight, or a held-back half-typed contact)? The live re-read
// waits until there's none.
export const hasUnsavedCaseEdit = (): boolean => {
  if (pendingPayload !== null || inFlightPromise !== null || pendingTimerId !== null) return true
  if (getHeldContact().length > 0) return true
  const open = useCasevoStore.getState().currentCase
  return !!open && lastSeededCaseId === open.id && lastSavedRaw !== null && JSON.stringify(open) !== lastSavedRaw
}
// adoptOpenCaseAsSaved: the case just re-read from the server IS the saved
// state — advance the diff baselines to it so the autosave effect doesn't
// send the other person's values back as "our" edit (the concurrency base
// was already set from the fresh row by setCurrentCaseById).
export const adoptOpenCaseAsSaved = (): void => {
  const open = useCasevoStore.getState().currentCase
  if (!open || pendingPayload !== null) return
  lastSeededCaseId = open.id
  lastSavedRaw = JSON.stringify(open)
  lastSavedFlat = flattenCase(open)
}

export const useAutoSave = () => {
  const currentCase = useCasevoStore((state) => state.currentCase)

  // beforeunload guard — fires when the user closes the tab/window or
  // hard-navigates away (e.g. typing a new URL). Triggers the browser's
  // native "Changes you may not be saved" dialog whenever there's an
  // edit still being debounced (pendingPayload) or in flight
  // (inFlightPromise). In-app navigation doesn't fire this — it goes
  // through flushAutoSave() which clears both before navigating.
  //
  // Modern browsers ignore any custom message; setting returnValue is
  // the legacy contract that tells them to show the prompt.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // (M17) A held-back half-typed email / phone is an unsaved change too.
      if (pendingPayload === null && inFlightPromise === null && getHeldContact().length === 0) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  useEffect(() => {
    // Do nothing if there is no case open (a held-back value went with it).
    if (!currentCase) { setHeldContact([]); return }

    const serialised = JSON.stringify(currentCase)

    // Seed the diff baseline when a DIFFERENT case loads (or the first case
    // this session). Loading isn't an edit, so we set the last-saved markers to
    // the freshly-loaded state and DON'T stage a save — this makes the first
    // real edit diff against the loaded values (only changed keys sent), and
    // avoids a spurious whole-case save when module-scope lastSavedRaw still
    // holds a previous case's value.
    if (currentCase.id !== lastSeededCaseId) {
      // ★ A different case loaded while an edit to the PREVIOUS case was
      // still pending (a nav path that didn't flushAutoSave first — linked
      // case, notification, context menu). Save it NOW against its own
      // baseline rather than leaving it to be diffed against the new case
      // (which sent a near-whole-case overwrite and corrupted the new case's
      // baseline) or silently dropped.
      if (pendingPayload && pendingPayload.id !== currentCase.id) {
        const orphan: PendingPayload = { ...pendingPayload, baseFlat: lastSavedFlat }
        pendingPayload = null
        if (pendingTimerId !== null) { clearTimeout(pendingTimerId); pendingTimerId = null }
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }
        // Serialise behind any in-flight save of case A — running both at
        // once made the orphan 409 against A's own just-landed write.
        const prior = inFlightPromise
        void (async () => {
          if (prior) { try { await prior } catch { /* logged in runSave */ } }
          await runSave(orphan, true)
        })()
      }
      lastSeededCaseId = currentCase.id
      lastSavedRaw = serialised
      lastSavedFlat = flattenCase(currentCase)
      setHeldContact([])
      return
    }

    // (M17) Is a half-typed email / phone being held back right now? Worked
    // out on every change (not only when the save fires), so leaving straight
    // after typing still asks, and nothing claims it was saved.
    const flatNow = flattenCase(currentCase)
    setHeldContact(heldContactOf(flatNow, lastSavedFlat))

    // Skip if nothing has changed since the last successful save
    if (serialised === lastSavedRaw) return

    // Stage the latest snapshot. Each keystroke replaces this so flush()
    // (or the debounced fire) always uses the freshest data.
    pendingPayload = {
      id: currentCase.id,
      flat: flatNow,
      serialised,
    }

    // A new keystroke supersedes any pending retry — the regular
    // debounce will pick up this fresher data shortly anyway.
    if (retryTimerId !== null) {
      clearTimeout(retryTimerId)
      retryTimerId = null
    }

    // Reset the debounce timer for normal typing.
    if (pendingTimerId !== null) clearTimeout(pendingTimerId)
    pendingTimerId = setTimeout(() => {
      pendingTimerId = null
      // Wait for any save still in flight, then send the FRESHEST pending
      // payload. Two overlapping saves made the second carry a stale base →
      // a 409 against our own first write.
      const prior = inFlightPromise
      void (async () => {
        if (prior) { try { await prior } catch { /* logged in runSave */ } }
        if (pendingPayload) await runSave(pendingPayload)
      })()
    }, 1000)

    // On unmount or before the next effect run, only clear the timer —
    // do NOT discard pendingPayload, because flushAutoSave() may still
    // need it (e.g. nav handler called flush() then immediately
    // clearCurrentCase() which causes this effect to re-run with no case).
    return () => {
      if (pendingTimerId !== null) {
        clearTimeout(pendingTimerId)
        pendingTimerId = null
      }
    }
  }, [currentCase])
}
